import type { FormModelField } from '../../form/form.field';
import { ModelMutator } from '../collection.field';
import type { CollectionModelFieldNumber } from './number.field';

export const NumberFieldProvider = {
	MANUAL: 'manual',
	BEARING: 'bearing',
	BACK_BEARING: 'backbearing',
	SAG: 'sag',
} as const;

/** Methods to modify a number field */
export class ModelMutatorNumber extends ModelMutator<FormModelField, CollectionModelFieldNumber> {
	/** Set the field provided by number */
	manual(newValue: number | string | null) {
		if (newValue == null) {
			this.state.setFieldValue(this.field.id, null);
			return;
		}
		let value: number;
		if (typeof newValue === 'string') {
			value = parseFloat(newValue);
		} else {
			value = newValue;
		}

		if (value == null || isNaN(value)) {
			this.state.setFieldValue(this.field.id, null);
			return;
		}

		this.state.setFieldValue(this.field.id, {
			provider: NumberFieldProvider.MANUAL,
			value,
		});
	}

	/** Set the value provided by bearing */
	bearing(captureId: string) {
		this.state.setFieldValue(this.field.id, {
			provider: NumberFieldProvider.BEARING,
			captureId,
		});
	}

	/** Set the field value provided by backbearing */
	backBearing(captureId: string) {
		this.state.setFieldValue(this.field.id, {
			provider: NumberFieldProvider.BACK_BEARING,
			captureId,
		});
	}

	/**
	 * Set the field value provided by sag
	 *
	 * @param start CaptureId of the starting point
	 * @param end CaptureId of the ending point
	 * @param points CaptureId of the mid points
	 */
	sag(start: string, end: string, points: string[]) {
		const pointIds: string[] = points.slice();

		this.state.setFieldValue(this.field.id, {
			provider: NumberFieldProvider.SAG,
			start,
			end,
			points: pointIds,
		});
	}

	/** Clone another field */
	override copy(from: CollectionModelFieldNumber): boolean {
		if (from.value == null) {
			this.manual(null);
			return true;
		}

		if (from.isManual()) {
			this.manual(from.value);
			return true;
		}

		if (from.isSag()) {
			const stateValue = from.stateValue;
			this.sag(stateValue.start, stateValue.end, stateValue.points);
			return true;
		}
		if (from.isBearing()) {
			this.bearing(from.stateValue.captureId);
			return true;
		}

		if (from.isBackBearing()) {
			this.backBearing(from.stateValue.captureId);
			return true;
		}

		return false;
	}

	reverse() {
		if (!this.field.hasValue()) {
			return;
		}

		if (this.field.isBearing()) {
			// TODO: IKE Field does not support 'bearing' provider so bearings are not flipped
			return;
		}

		if (this.field.isBackBearing()) {
			// TODO: IKE Field does not support 'bearing' provider so bearings are not flipped
			return;
		}

		if (this.field.isSag()) {
			const stateValue = this.field.stateValue;
			this.sag(stateValue.end, stateValue.start, stateValue.points.slice());
			return;
		}

		// Manual numbers are not flipped
	}
}
